/**
 * Renders a single row in the sprint list table, including sprint info, assigned user,
 * category, recurrence details, backlog stats, and action buttons with permissions.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} props.id - The ID of the sprint to render.
 * @param {boolean} [props.isFirst] - Whether this is the first item in the list (used for targeting).
 * @returns {JSX.Element} - The rendered table row.
 */

import React, { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import PropTypes from 'prop-types'
import { v4 as uuidv4 } from 'uuid'
import { observer } from 'mobx-react-lite'
import { Button, Popconfirm, Popover, Select, Space, Tag, Tooltip } from 'antd'
import { UserOutlined, InfoOutlined, CalendarOutlined } from '@ant-design/icons'

import {
	actLoadSprint,
	actSaveSprint,
	actToTeamSprint,
	newSprint,
	requireSprintById
} from '../../../stores/sprints/actions'
import {
	actUndoOrDeleteSprint,
	actLoadSprints
} from '../../../stores/ui-lst-sprints/actions'
import { useStores } from '../../../cls/stores/MobXProvider'

import {
	StatusTag,
	Filter,
	UserString,
	LstTable,
	FormatedTimestamp,
	BetweenDates,
	CustomTable,
	Category
} from '../../../components/Shared'
import { recurrenceLabels } from '../../../components/Form/recurrence/labels'
import ProgressModal from '../../../components/Form/ProgressModal'
import { runInAction } from 'mobx'
import {
	GeneralContentWrapper,
	GeneralTourTitle
} from '../../../components/Shared/GeneralComponents'
import { getTourArrayByKeys } from '../../../stores/tour/config'
import { DueIn } from '../../../components/Shared/FormatedTimestamp'
import dayjs from 'dayjs'
import { sendNotification } from '../../../stores/notifications/action'
import { translate as t } from '../../../cls/TranslationContext'
import { requiredVersion } from '../../../config/versionUtils'
import { useSmartTranslation } from '../../../cls/TranslationContext'
import { startError } from '../../../stores/work-progress/actions'
/*
 * Sprint's can be multiple types
 */
export const LstItem = observer(({ id, isFirst }) => {
	const { t, i18n } = useSmartTranslation()
	const curentLanguage = i18n.language

	const navigate = useNavigate()
	const { uiLstSprints, users, appType } = useStores()
	const { loggedInUser } = users
	const { appIsClient, appIsTeam } = appType
	const isAdmin = loggedInUser.isAdmin
	const isSuperAdmin = loggedInUser.isSuperAdmin

	const lst = appIsClient ? 'sprints' : appIsTeam ? 'sprints_team' : ''

	const sprint = requireSprintById(id)
	const selfPermission =
		sprint.createdBy === loggedInUser.user_id || isAdmin || isSuperAdmin

	const hasRecurrence = !!sprint.recurrence.next
	const hasBacklog = !!sprint.sprint_team_count

	// Delete is active only for the creator of the sprint if is in draft mode or if the user is an admin
	const deleteIsDisabled = !isAdmin
		? !(
				sprint.progress === 'draft' &&
				loggedInUser.user_id === sprint.createdBy
		  )
		: false

	const [showConfirmUndoOrDelete, setConfirmUndoOrDelete] = useState(false)
	const confirmUndoOrDelete = () => {
		setConfirmUndoOrDelete(false)

		if (!!sprint.to_delete) {
			actUndoOrDeleteSprint(id, false).catch(console.error)
		} else {
			actUndoOrDeleteSprint(id, true).catch(console.error)
		}
	}
	const _actions = [
		{
			key: 'status',
			label: t('single.set-progress'),
			value: 'status',
			disabled: !selfPermission && !users.canUser('write', lst)
		},
		{
			key: 'view',
			label: t('single.view'),
			value: 'view',
			disabled: !selfPermission && !users.canUser('read', lst)
		},
		{
			key: 'data',
			label: t('single.edit'),
			value: 'data',
			disabled:
				(!!sprint.to_delete || !selfPermission) &&
				!users.canUser('write', lst)
		}
	]

	// Delete / Undo actions
	if (selfPermission) {
		if (sprint.to_delete) {
			_actions.push({
				key: 'undo-del',
				label: t('single.delete-undo'),
				value: 'undo-delete',
				disabled: deleteIsDisabled && !users.canUser('delete', lst)
			})
		} else {
			_actions.push({
				key: 'del',
				label: t('single.delete'),
				value: 'delete',
				disabled: deleteIsDisabled && !users.canUser('delete', lst)
			})
		}
	}

	// To team action
	if ((isAdmin || isSuperAdmin) && appType.appIsClient) {
		_actions.push({
			key: 'to-team',
			label: t('single.team.assign'),
			value: 'to-team'
		})
	}

	const handleActions = (value) => {
		switch (value) {
			case 'view':
				navigate(`${id}/view`)
				break
			case 'data':
				navigate(`${id}/edit`)
				break
			case 'undo-delete':
				setConfirmUndoOrDelete(true)
				break
			case 'delete':
				setConfirmUndoOrDelete(true)
				break
			case 'status':
				runInAction(() => {
					uiLstSprints.setModal('sprint-progress', {
						visible: true,
						itmId: id
					})
				})
				break
			case 'to-team':
				actToTeamSprint(id).catch(console.error)
			default:
				break
		}
	}

	return (
		<tr>
			<td>
				<CustomTable
					data={[
						[
							{
								item: (
									<Space style={{ width: '100%' }}>
										<StatusTag
											id={
												isFirst
													? 'target-itm-priority'
													: ''
											}
											tag={sprint.priority}
											tagType={'PriorityTag'}
											style={{
												marginRight: 0,
												padding: '0 3px'
											}}
										/>
										<StatusTag
											isShort
											id={
												isFirst
													? 'target-itm-progress'
													: ''
											}
											tag={sprint.progress}
											style={{
												marginRight: 0,
												padding: '0 3px'
											}}
										/>
										<Tooltip title={t('__view%_item')}>
											<Link
												to={`${id}/view`}
												id={
													isFirst
														? 'target-itm-link'
														: ''
												}
											>
												{sprint.name}
											</Link>
										</Tooltip>
									</Space>
								)
							}
						]
					]}
				/>
			</td>
			<td>
				<CustomTable
					data={[
						[
							// 'For user:',
							{
								item:
									loggedInUser.user_id ===
									sprint.for_user_id ? (
										<Tooltip
											title={t(
												'__assigned%single.to-you'
											)}
										>
											<UserOutlined
												id={
													isFirst
														? 'target-itm-user'
														: ''
												}
											/>
										</Tooltip>
									) : (
										<Tooltip
											title={t(
												'__assigned-to%_user.possessive.single'
											)}
										>
											<Tag
												color="default"
												id={
													isFirst
														? 'target-itm-user'
														: ''
												}
											>
												<UserString
													userId={sprint.for_user_id}
													path="name"
												/>
											</Tag>
										</Tooltip>
									)
							}
						],
						[
							// 'Category:',
							{
								item: (
									<Tooltip title={t('single.category')}>
										<span
											id={
												isFirst
													? 'target-itm-category'
													: ''
											}
										>
											<Category
												categoryId={sprint.category_id}
												k={curentLanguage}
											/>
										</span>
									</Tooltip>
								)
							}
						]
					]}
				/>
			</td>
			<td id={isFirst ? 'target-itm-recurrence' : ''}>
				{sprint.recurrence.type && (
					<CustomTable
						data={[
							[
								t('single.next'),
								{
									item: sprint.recurrence.next ? (
										<FormatedTimestamp
											timestamp={sprint.recurrence.next}
										/>
									) : sprint.progress !== 'completed' ? (
										t('single.requires-update')
									) : (
										t('single.completed')
									)
								}
							],
							sprint.recurrence.next &&
								sprint.progress !== 'completed' && [
									t('single.in'),
									{
										item: (
											<DueIn
												timestamp={
													sprint.recurrence.next
												}
											/>
										)
									}
								]
						]}
					/>
				)}
			</td>

			<td>
				<Space
					align="end"
					style={{ width: '100%' }}
					direction="vertical"
				>
					<Space align="end" style={{ width: '100%' }}>
						<Popover
							content={() => (
								<CustomTable
									data={[
										[
											{
												item: (
													<strong>
														{t('single.info')}
													</strong>
												)
											}
										],
										[
											t('single.updated-last'),
											{
												item: (
													<FormatedTimestamp
														timestamp={
															sprint.modifiedTs
														}
														formatedAs="DD.MM.YYYY hh:mm"
													/>
												)
											}
										],
										[
											t('single.created-at'),
											{
												item: (
													<FormatedTimestamp
														timestamp={
															sprint.createdTs
														}
														formatedAs="DD.MM.YYYY hh:mm"
													/>
												)
											}
										],
										[
											t('single.created-by.label'),
											{
												item: (
													<UserString
														userId={
															sprint.createdBy
														}
														path="name"
													/>
												)
											}
										],
										[
											t('__assigned-to'),
											{
												item: (
													<UserString
														userId={
															sprint.for_user_id
														}
														path="name"
													/>
												)
											}
										],
										hasBacklog && [
											{
												item: (
													<strong>
														{t('single.backlog')}
													</strong>
												)
											}
										],
										hasBacklog && [
											t('single.tasks-n'),
											{
												item: (
													<span>
														{
															sprint.sprint_team_count
														}
													</span>
												)
											}
										],
										hasBacklog && [
											t('single.progress'),
											{
												item: (
													<span>
														{sprint.progress_team}
													</span>
												)
											}
										],
										hasRecurrence && [
											{
												item: (
													<strong>
														{t(
															'single.recurrence.label'
														)}
													</strong>
												)
											}
										],
										hasRecurrence && [
											t('single.type'),
											{
												item:
													recurrenceLabels[
														sprint.recurrence.type
													] ||
													'*' + sprint.recurrence.type
											}
										],
										hasRecurrence && [
											t('single.next'),
											{
												item: sprint.recurrence.next ? (
													<FormatedTimestamp
														timestamp={
															sprint.recurrence
																.next
														}
													/>
												) : sprint.progress !==
												  'completed' ? (
													t('single.requires-update')
												) : (
													t('single.completed')
												)
											}
										],
										hasRecurrence &&
											sprint.recurrence.type !==
												'deadline' && [
												t('single.start-end.label'),
												{
													item: (
														<BetweenDates
															timestamps={
																sprint
																	.recurrence
																	.startEnd
															}
														/>
													)
												}
											]
									]}
								/>
							)}
							trigger="hover"
						>
							<Button
								id={isFirst ? 'target-itm-info' : ''}
								color="default"
								icon={<InfoOutlined />}
							/>
						</Popover>
						<Select
							placement="topRight"
							id={isFirst ? 'target-itm-actions' : ''}
							value={t('single.actions')}
							options={_actions}
							onChange={handleActions}
							style={{ width: 120 }}
							popupMatchSelectWidth={false}
						/>
					</Space>
					{
						// Check if the user has permission to view delete information
						// (either through selfPermission or a specific permission)
						// and if the sprint has a timestamp for deletion
						(selfPermission || users.canUser('delete_view', lst)) &&
							sprint.toDeleteTs && (
								<>
									<span>
										{t('single.delete-on')}
										<FormatedTimestamp
											timestamp={sprint.toDeleteTs}
										/>
									</span>
								</>
							)
					}
				</Space>

				<Popconfirm
					title={
						sprint.to_delete
							? t('single.confirm-undo-delete')
							: t('single.confirm-delete')
					}
					open={showConfirmUndoOrDelete}
					onConfirm={confirmUndoOrDelete}
					onCancel={() => setConfirmUndoOrDelete(false)}
					okType="link"
					placement="topLeft"
				/>
			</td>
		</tr>
	)
})

LstItem.propTypes = {
	id: PropTypes.string.isRequired,
	isFirst: PropTypes.bool
}

export const sprintsLstProgressOptions = [
	{
		key: 'by-progress',
		value: '',
		label: t('__select.item%single.status')
	},
	{
		key: 'by-progress-canceled',
		value: 'canceled',
		label: t('single.canceled')
	},
	{
		key: 'by-progress-draft',
		value: 'draft',
		label: t('single.draft')
	},
	{
		key: 'by-progress-in_review',
		value: 'in_review',
		label: t('single.in-review')
	},
	{
		key: 'by-progress-active',
		value: 'active',
		label: t('single.active')
	},
	{
		key: 'by-progress-pending',
		value: 'pending',
		label: t('single.pending')
	},
	{
		key: 'by-progress-completed',
		value: 'completed',
		label: t('single.completed')
	}
]

const SprintsLst = observer(() => {
	const { t } = useSmartTranslation()
	const navigate = useNavigate()
	const { uiLstSprints, tourStore, appType, users } = useStores()
	const { appIsClient, appIsTeam } = appType
	const { loggedInUser, loggedInUserId } = users
	const isAdmin = loggedInUser.isAdmin
	const isSuperAdmin = loggedInUser.isSuperAdmin
	const pg = uiLstSprints.pg
	const bySearch = uiLstSprints.search
	const byDel = uiLstSprints.del
	const byCategory = uiLstSprints.filter.category
	const byProgress = uiLstSprints.filter.progress
	const byPriority = uiLstSprints.filter.priority
	const for_user_uid = uiLstSprints.filter.for_user_uid
	const user_uid = uiLstSprints.filter.user_uid
	const orderBy = uiLstSprints.filter.orderBy
	const orderDir = uiLstSprints.filter.orderDir

	// Determine th table entity for notification based on the app Type
	let tableEntity = appIsClient ? 'sprints' : appIsTeam ? 'sprints_team' : ''

	useEffect(() => {
		actLoadSprints().catch(console.error)
	}, [
		pg,
		bySearch,
		byDel,
		byCategory,
		byProgress,
		byPriority,
		user_uid,
		for_user_uid,
		orderBy,
		orderDir,
		appType.appIs
	])

	// generate tour item that will be hidden based on the permissions
	const hideTourItems = []
	const hideFilters = []
	if (
		!(isAdmin || isSuperAdmin || users.canUser('delete_view', tableEntity))
	) {
		hideFilters.push('deleted')
		hideTourItems.push('delete')
	}

	if (!(isAdmin || isSuperAdmin || users.canUser('write', tableEntity))) {
		hideFilters.push('addNew')
		hideTourItems.push('new')
	}

	const tourSprintLst = getTourArrayByKeys('sprintLst', ['all'])
	const tourFilter = getTourArrayByKeys(
		'filter',
		[
			'section',
			'search',
			'progress',
			'priority',
			'category',
			'new',
			'toggleBtn',
			'ByUser',
			'ForUser',
			'orderBy',
			'delete'
		],
		hideTourItems
	)

	/**
	 * Start tour:
	 * This function starts the tour for the sprint form.
	 * It includes the general form, subtasks, comment and recurrence fields.
	 */
	const startTour = () => {
		runInAction(() => {
			// Open advance filter when we start tour
			uiLstSprints.filterExpanded = true
		})

		const hasItems = uiLstSprints.ids.length > 0
		if (!hasItems) {
			const newId = uuidv4()
			tourStore.setDeleteItemAct(() =>
				actUndoOrDeleteSprint(newId, true, true).catch(console.error)
			)

			const demoObj = {
				createdTs: dayjs().unix(),
				createdBy: loggedInUserId,
				for_user_id: loggedInUserId,
				platformIds: [],
				category_id: byCategory || '7',
				name: 'demoItem',
				progress: 'draft',
				recurrence: {
					daysOfWeek: [],
					dayOfMonth: '',
					months: [],
					next: ''
				},
				subtasks: [],
				comments: []
			}
			actSaveSprint(newId, demoObj, true).catch(console.error)
		}
		tourStore.startTour('SprintsLst-tour', [
			requiredVersion('2.0.0') && {
				title: t('single.calendar.go-to'),
				description: t('single.calendar.description'),
				target: () => document.getElementById('target-calendar-view')
			},
			...tourFilter,
			...tourSprintLst
		])
	}

	const [addNotified, setAddNotified] = React.useState(false)
	useEffect(() => {
		if (!addNotified && !users.canUser('write', tableEntity)) {
			startError(t('single.sprint-required-write-permission'))
			setAddNotified(true)
		}
	}, [addNotified])
	return (
		<GeneralContentWrapper>
			<GeneralTourTitle
				titleProps={{ text: t('__lst.title%_sprint.multiple') }}
				buttonProps={{
					onClick: startTour
				}}
				rowProps={{
					justify: 'space-between'
				}}
			/>

			{requiredVersion('2.0.0') && (
				<Button
					className="no-print"
					id="target-calendar-view"
					type="primary"
					icon={<CalendarOutlined />}
					style={{ marginBottom: 16 }}
					onClick={() => navigate('calendar')}
				>
					{t('single.calendar.go-to')}
				</Button>
			)}
			<Filter
				uiStore={uiLstSprints}
				hide={hideFilters}
				addNew={() => {
					const newId = uuidv4()
					newSprint(newId).then(() => {
						navigate(`/sprints/${newId}/edit`)
					})
				}}
				progressOptions={sprintsLstProgressOptions}
			/>

			<ProgressModal
				type="sprint"
				modalKey="sprint-progress"
				uiStore={uiLstSprints}
				requireItemById={requireSprintById}
				loadItm={(id) => actLoadSprint(id)}
				saveItem={(itemId, itemValue) => {
					actSaveSprint(itemId, itemValue)

					// Create notification only if createdBy is not equal to for_user_id
					if (itemValue?.for_user_id != itemValue.createdBy) {
						const forUser = users.requireUserById(
							itemValue.for_user_id
						)
						sendNotification(
							loggedInUser,
							forUser?.email,
							itemValue?.for_user_id,
							tableEntity,
							itemId,
							'progress'
						)
					}
				}}
			/>

			<LstTable
				LstItem={LstItem}
				store={uiLstSprints}
				className="table-pro"
			>
				<tr id="items-lst">
					<th id="target-sprint">{t('single.overview')}</th>
					<th id="target-info">{t('single.information')}</th>
					<th id="target-recurrence">{t('_recurrence')}</th>
					<th id="target-actions">{t('single.actions')}</th>
				</tr>
			</LstTable>
		</GeneralContentWrapper>
	)
})

export default SprintsLst
